<script setup lang="ts">
import type { TableColumn, TableRow } from '@nuxt/ui'
import type { Product } from '@/types'
import { Head, Link, router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { dashboardLayout } from '@/lib/layouts'
import { adminProductPath, newAdminProductPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  products: Product[]
}>()

const title = 'Products'
const search = ref('')

const filtered = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query)
    return props.products

  return props.products.filter(product =>
    product.name.toLowerCase().includes(query)
    || product.slug.toLowerCase().includes(query),
  )
})

const columns: TableColumn<Product>[] = [
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'slug', header: 'Slug' },
  { accessorKey: 'created_at', header: 'Created' },
  { id: 'actions' },
]

function openProduct(_event: Event, row: TableRow<Product>) {
  router.visit(adminProductPath(row.original.slug))
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString()
}
</script>

<template>
  <Head :title="title" />

  <UDashboardPanel id="products">
    <template #header>
      <UDashboardNavbar :title="title">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>

        <template #right>
          <UButton
            :to="newAdminProductPath()"
            :as="Link"
            label="New product"
            icon="i-lucide-plus"
          />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div
        v-if="products.length === 0"
        class="flex flex-col items-center justify-center gap-4 py-20 text-center"
      >
        <UIcon name="i-lucide-box" class="text-dimmed size-10" />
        <div>
          <p class="text-highlighted font-medium">
            No products yet
          </p>
          <p class="text-muted text-sm">
            Create your first product to start issuing licenses and updates.
          </p>
        </div>
        <UButton
          :to="newAdminProductPath()"
          :as="Link"
          label="New product"
          icon="i-lucide-plus"
        />
      </div>

      <div v-else class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-4">
          <UInput
            v-model="search"
            icon="i-lucide-search"
            placeholder="Search products…"
            class="w-full max-w-sm"
          />
          <p class="text-muted shrink-0 text-sm tabular-nums">
            {{ filtered.length }} of {{ products.length }}
          </p>
        </div>

        <UTable
          :data="filtered"
          :columns="columns"
          :on-select="openProduct"
          class="ring-default rounded-lg ring"
          :ui="{ tr: 'cursor-pointer' }"
        >
          <template #name-cell="{ row }">
            <span class="text-highlighted font-medium">{{ row.original.name }}</span>
          </template>

          <template #slug-cell="{ row }">
            <span class="text-muted font-mono text-sm">{{ row.original.slug }}</span>
          </template>

          <template #created_at-cell="{ row }">
            <span class="text-muted text-sm">{{ formatDate(row.original.created_at) }}</span>
          </template>

          <template #actions-header>
            <span class="sr-only">Actions</span>
          </template>

          <template #actions-cell>
            <div class="flex justify-end">
              <UIcon name="i-lucide-chevron-right" class="text-dimmed size-4" />
            </div>
          </template>

          <template #empty>
            <div class="text-muted py-6 text-center text-sm">
              No products match “{{ search }}”.
            </div>
          </template>
        </UTable>
      </div>
    </template>
  </UDashboardPanel>
</template>
